import React, { useEffect } from 'react';

import { CalendarOutlined } from '@ant-design/icons';
import { Badge, Flex, Image, List, Typography } from 'antd';
import userPlaceholder from 'assets/placeholder-image.webp';
import cn from 'classnames';
import { BASE_URL, USER_AVATAR_FOLDER } from 'config';
import dayjs from 'dayjs';
import { useIntersectionObserver } from 'hooks/reactuse';
import { type User } from 'models/user';
import { AiOutlineBell } from 'react-icons/ai';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from 'services/redux/configureStore';

import { type Notification } from '../../../api/types/notification';
import styles from '../NotificationsWidget.module.css';

const { Text } = Typography;

interface NotificationItemProps {
  notification: Notification;
  onRead: (id: number) => void;
  currentUser: User;
}

export const NotificationItem = ({ notification, onRead, currentUser }: NotificationItemProps) => {
  const navigate = useNavigate();

  const { ref, inView } = useIntersectionObserver<HTMLDivElement>({ threshold: 0.65, triggerOnce: true });

  const isDarkMode = useAppSelector((state) => state.theme.isDarkMode);

  useEffect(() => {
    if (!inView || notification.isRead) return;

    const timer = setTimeout(() => {
      onRead(notification.id);
    }, 1500);

    return () => clearTimeout(timer);
  }, [inView]);

  const userPlaceholderComponent = <Image width={35} height={35} src={userPlaceholder} preview={false} />;
  const regularPlaceholderComponent = (
    <AiOutlineBell width={35} height={35} className={styles.imagePlaceholder} />
  );

  const Placeholder = ['BIRTHDAY_TODAY', 'BIRTHDAY_SOON'].includes(notification.event.type)
    ? userPlaceholderComponent
    : regularPlaceholderComponent;

  const canOpenUser = () => {
    const targetId = notification.event.target.id;
    const assignedIds = currentUser.assignedUsers?.map((u) => u.id);

    const isBirthday = ['BIRTHDAY_TODAY', 'BIRTHDAY_SOON'].includes(notification.event.type);
    const isAssigned = targetId && assignedIds?.includes(targetId);
    const isAdmin = currentUser.role === 'ADMIN';

    return isBirthday && (isAssigned || isAdmin);
  };

  return (
    <List.Item
      className={cn(styles.item, {
        [styles.darkBg]: !notification.isRead && !isDarkMode,
        [styles.lightBg]: !notification.isRead && isDarkMode,
      })}
      ref={ref}
    >
      <Flex vertical gap={3} style={{ width: '100%' }}>
        <Flex className={styles.notificationContent}>
          <Badge dot={!notification.isRead} offset={[-3, 3]}>
            {notification.event.target.image ? (
              <Image
                width={35}
                height={35}
                src={`${BASE_URL}${USER_AVATAR_FOLDER}/${notification.event.target.image}`}
                className={styles.notificationsImage}
              />
            ) : (
              Placeholder
            )}
          </Badge>
          {canOpenUser() ? (
            <Text
              onClick={() => navigate(`/users/${notification.event.target.id}`)}
              style={{ cursor: 'pointer' }}
            >
              {notification.message}
            </Text>
          ) : (
            <Text>{notification.message}</Text>
          )}
        </Flex>
        <Flex justify={'end'} gap={5} style={{ flexShrink: 0, width: '100%' }}>
          <CalendarOutlined className={styles.calendarImage} />
          <Text type={'secondary'} className={styles.notifyDate}>
            {dayjs(notification.notifyDate).format('DD MMMM YYYY')}
          </Text>
        </Flex>
      </Flex>
    </List.Item>
  );
};
